/**
 * @fileoverview Main Operator Administration Board for Personal Quick Reaction ("Reacción Rápida Personal" - Tile ID 45).
 * Touch-friendly control panel for event operators, production teams, and referees.
 * Features:
 *   - 10-KPI Real-Time Funnel Telemetry Row with horizontal scrolling.
 *   - Live Broadcast Overlay controls (Modes: invitation/winner, 1 QR / 2 QRs, Offset Y slider, banner uploads).
 *   - Gameplay & Physics Configuration (Anti-spam strategies, tension delays, biological threshold, shared device).
 *   - SkiaSharp Theme Management integration.
 *   - Interactive Participant Table with search, filters, PII toggle, pagination, soft-resets, and multi-sheet Excel export.
 *   - Protected Danger Zone with master password ("").
 *   - Native ADSL WebSocket synchronization.
 * 
 * @module components/Admin/Overlays/QuickReactionPersonal/QuickReactionPersonalBoard
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { SecondaryBase } from './../../Commons/SecondaryBase';
import { Confirm } from './../../../Commons/Modals/Confirm';

import {
    GetAdminData,
    SaveConfig as BLSaveConfig,
    SaveOverlayConfig as BLSaveOverlayConfig,
    UploadAsset,
    ExportPersonalQuickReactionToExcel,
    MaskEmail,
    MaskPhone,
    resolveMediaUrl
} from './../../../../utilities/BL/PersonalQuickReaction';

import {
    ConnectPersonalQuickReactionAdminWS,
    DisconnectPersonalQuickReactionAdminWS
} from './../../../../utilities/WS/Admin/PersonalQuickReactionWS';

import { QuickReactionPersonalMetricsRow } from './QuickReactionPersonalMetricsRow';
import { QuickReactionPersonalThemeModal } from './QuickReactionPersonalThemeModal';
import { QuickReactionPersonalResetModal } from './QuickReactionPersonalResetModal';
import { QuickReactionPersonalDangerModal } from './QuickReactionPersonalDangerModal';

/**
 * Throttle utility for continuous slider changes.
 */
function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => { inThrottle = false; }, limit);
        }
    };
}

/**
 * Personal Quick Reaction Operator Admin Panel Component.
 * 
 * @function QuickReactionPersonalBoard
 * @param {Object} props
 * @param {Function} props.getOutOfTheDynamic - Exit dynamic callback to return to Dashboard.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const QuickReactionPersonalBoard = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Core Data States
    const [roomId] = useState('00000000-0000-0000-0000-000000000000');
    const [participants, setParticipants] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [themesList, setThemesList] = useState([]);
    const [funnelMetrics, setFunnelMetrics] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState('');

    // Gameplay Configuration State
    const [gameConfig, setGameConfig] = useState({
        isActive: true,
        earlyFaultStrategy: 'NONE', // NONE, THROTTLE, RESET_DELAY, TIME_PENALTY, STRICT_FAULT
        allowEarlyFaultDetection: false,
        minWaitDelayMs: 3000,
        maxWaitDelayMs: 6000,
        inactivityTimeoutSeconds: 7,
        minHumanReactionThresholdMs: 120,
        allowSharedDevice: true,
        activeThemeId: null
    });

    // Live Broadcast Overlay State
    const [overlayConfig, setOverlayConfig] = useState({
        overlayVisible: false,
        overlayMode: 'invitation', // invitation, winner
        overlayQrCount: 1, // 1, 2
        overlayOffsetY: 0,
        overlayBanner1536Url: '',
        overlayBanner1920Url: ''
    });

    // PII Masking toggle
    const [maskPii, setMaskPii] = useState(true);

    // Search and Table Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, winners, early_loss, disqualified, retries
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Modals Control
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [resetTargetParticipant, setResetTargetParticipant] = useState(null);
    const [isDangerModalOpen, setIsDangerModalOpen] = useState(false);

    // Confirm Modal
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });

    // Accordion expand states
    const [isOverlayAccordionOpen, setIsOverlayAccordionOpen] = useState(true);
    const [isPhysicsAccordionOpen, setIsPhysicsAccordionOpen] = useState(false);
    const [isThemesAccordionOpen, setIsThemesAccordionOpen] = useState(false);

    // Throttled overlay offset updater
    const throttledSaveOverlayOffset = useRef(
        throttle(async (offsetVal, currentOverlayState) => {
            await BLSaveOverlayConfig({
                roomId,
                ...currentOverlayState,
                overlayOffsetY: offsetVal
            });
        }, 200)
    ).current;

    // Load complete administrative snapshot
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminData(roomId);
            if (res.success && res.data) {
                const data = res.data;
                if (data.config) {
                    setGameConfig({
                        isActive: data.config.isActive ?? true,
                        earlyFaultStrategy: data.config.earlyFaultStrategy || 'NONE',
                        allowEarlyFaultDetection: data.config.allowEarlyFaultDetection ?? false,
                        minWaitDelayMs: data.config.minWaitDelayMs || 3000,
                        maxWaitDelayMs: data.config.maxWaitDelayMs || 6000,
                        inactivityTimeoutSeconds: data.config.inactivityTimeoutSeconds || 7,
                        minHumanReactionThresholdMs: data.config.minHumanReactionThresholdMs || 120,
                        allowSharedDevice: data.config.allowSharedDevice ?? true,
                        activeThemeId: data.config.activeThemeId || null
                    });

                    setOverlayConfig({
                        overlayVisible: data.config.overlayVisible ?? false,
                        overlayMode: data.config.overlayMode || 'invitation',
                        overlayQrCount: data.config.overlayQrCount || 1,
                        overlayOffsetY: data.config.overlayOffsetY || 0,
                        overlayBanner1536Url: data.config.overlayBanner1536Url || '',
                        overlayBanner1920Url: data.config.overlayBanner1920Url || ''
                    });
                }

                if (Array.isArray(data.participants)) {
                    setParticipants(data.participants);
                }

                if (Array.isArray(data.auditLogs)) {
                    setAuditLogs(data.auditLogs);
                }

                if (Array.isArray(data.themes)) {
                    setThemesList(data.themes);
                }

                if (data.funnelMetrics) {
                    setFunnelMetrics(data.funnelMetrics);
                }

                setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }) + ' hrs');
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al cargar datos del panel.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de conexión.' });
        } finally {
            setIsLoading(false);
        }
    }, [roomId, showMessage]);

    // WebSocket Real-Time Listener
    useEffect(() => {
        loadData();

        ConnectPersonalQuickReactionAdminWS({
            onNewPlay: (playData) => {
                showMessage({
                    type: MESSAGE_TYPE.INFO,
                    text: `⚡ Nueva jugada: ${playData.participantName} (${playData.reactionTimeMs} ms)`
                });
                loadData();
            },
            onConfigUpdated: () => {
                loadData();
            },
            onReset: () => {
                showMessage({
                    type: MESSAGE_TYPE.WARNING,
                    text: '⚠️ La sala ha sido reiniciada por otro operador.'
                });
                loadData();
            }
        });

        return () => {
            DisconnectPersonalQuickReactionAdminWS();
        };
    }, [loadData, showMessage]);

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        } else {
            window.location.href = '/admin/dashboard';
        }
    };

    // Toggle Overlay Visibility
    const handleToggleOverlayVisibility = (nextVisibility) => {
        setConfirmConfig({
            message: `¿Estás seguro de ${nextVisibility ? 'MOSTRAR' : 'OCULTAR'} el Live Overlay de Reacción Rápida en las pantallas de transmisión?`,
            textYes: nextVisibility ? 'Sí, mostrar overlay' : 'Sí, ocultar overlay',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando visibilidad del overlay...' });
                const nextConfig = { ...overlayConfig, overlayVisible: nextVisibility };
                const res = await BLSaveOverlayConfig({ roomId, ...nextConfig });
                if (res.success) {
                    setOverlayConfig(nextConfig);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `El Live Overlay ahora está ${nextVisibility ? 'VISIBLE' : 'OCULTO'} en transmisiones.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    // Save Overlay Configuration (Mode, QR Count, OffsetY)
    const handleSaveOverlaySettings = async () => {
        setIsLoading(true);
        try {
            const res = await BLSaveOverlayConfig({ roomId, ...overlayConfig });
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Configuración del Live Overlay guardada y sincronizada en tiempo real!'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar overlay.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de conexión.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Save Game Physics & Rules
    const handleSaveGamePhysics = async (e) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        try {
            const res = await BLSaveConfig({ roomId, ...gameConfig });
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Físicas y reglas de juego guardadas con éxito!'
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar físicas.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de conexión.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Upload Banner directly in Overlay Accordion
    const handleUploadOverlayBanner = async (file, bannerType) => {
        try {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Optimizando cintillo con SkiaSharp...' });
            const res = await UploadAsset(file, roomId, bannerType);
            if (res.success && res.data?.url) {
                const updated = {
                    ...overlayConfig,
                    [bannerType === 'banner_1536' ? 'overlayBanner1536Url' : 'overlayBanner1920Url']: res.data.url
                };
                setOverlayConfig(updated);
                await BLSaveOverlayConfig({ roomId, ...updated });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Cintillo "${file.name}" cargado y optimizado correctamente.`
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al procesar cintillo.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error de red.' });
        }
    };

    // Export to Excel Multi-Sheet
    const handleExportExcel = () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte Excel de 4 hojas...' });
        const success = ExportPersonalQuickReactionToExcel({
            participants,
            funnelMetrics,
            auditLogs
        }, 'Reacción Rápida Personal');

        if (success) {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: '¡Reporte Excel (.xlsx) descargado exitosamente con 4 hojas completas!'
            });
        } else {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No hay datos suficientes para generar el reporte Excel.'
            });
        }
    };

    // Filter participants
    const filteredParticipants = participants.filter((p) => {
        const name = (p.participantName || p.name || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        const phone = (p.phone || '');
        const query = searchTerm.toLowerCase();

        const matchesQuery = !searchTerm || name.includes(query) || email.includes(query) || phone.includes(query);

        let matchesStatus = true;
        if (statusFilter === 'winners') {
            matchesStatus = p.isWinner || (p.rankPosition === 1);
        } else if (statusFilter === 'early_loss') {
            matchesStatus = p.isEarlyFault || p.outcomeStatus === 'EARLY_LOSS';
        } else if (statusFilter === 'disqualified') {
            matchesStatus = p.outcomeStatus === 'DISQUALIFIED';
        } else if (statusFilter === 'retries') {
            matchesStatus = p.resetByAdmin;
        }

        return matchesQuery && matchesStatus;
    });

    // Pagination
    const totalRegistries = filteredParticipants.length;
    const totalPages = Math.ceil(totalRegistries / pageSize) || 1;
    const verifiedPage = Math.min(currentPage, totalPages);
    const startIndex = (verifiedPage - 1) * pageSize;
    const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + pageSize);

    const activeTheme = themesList.find(t => t.id === gameConfig.activeThemeId) || null;

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {/* Modal de Confirmación Rápida */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                />
            )}

            {/* Modal de Gestor de Temas */}
            <QuickReactionPersonalThemeModal
                isOpen={isThemeModalOpen}
                onClose={() => { setIsThemeModalOpen(false); loadData(); }}
                roomId={roomId}
                activeThemeId={gameConfig.activeThemeId}
                onThemeSelected={async (themeId) => {
                    const updated = { ...gameConfig, activeThemeId: themeId };
                    setGameConfig(updated);
                    await BLSaveConfig({ roomId, ...updated });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema activo actualizado con éxito.' });
                    setIsThemeModalOpen(false);
                    loadData();
                }}
            />

            {/* Modal de Soft-Reset ("Dar otra oportunidad") */}
            <QuickReactionPersonalResetModal
                isOpen={!!resetTargetParticipant}
                participant={resetTargetParticipant}
                onClose={() => setResetTargetParticipant(null)}
                onResetSuccess={() => {
                    setResetTargetParticipant(null);
                    loadData();
                }}
            />

            {/* Modal de Zona Peligrosa */}
            <QuickReactionPersonalDangerModal
                isOpen={isDangerModalOpen}
                roomId={roomId}
                onClose={() => setIsDangerModalOpen(false)}
                onFullResetSuccess={() => {
                    setIsDangerModalOpen(false);
                    loadData();
                }}
            />

            <div className="container-fluid px-4 py-3 QuickReactionPersonalBoard">
                {/* Header Principal */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-lightning-fill text-warning"></i>
                            Reacción Rápida Personal
                            <span className="badge bg-warning text-dark ms-2 fs-6 fw-bold">
                                Tile 45 (Overlays)
                            </span>
                        </h1>
                        <p className="text-secondary small mb-0">
                            Consola Operativa Touch-Friendly | Ruta Fan: <code>/psl-qreaction</code> | Overlay: <code>/abcdef/overlays/quick-reaction</code>
                            {lastUpdated && <span className="ms-2">| Última sync: <strong>{lastUpdated}</strong></span>}
                        </p>
                    </div>

                    {/* Botones de Cabecera */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-outline-secondary fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={loadData}
                            disabled={isLoading}
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin' : ''}`}></i>
                            Recargar Datos
                        </button>

                        <button
                            type="button"
                            className="btn btn-success fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleExportExcel}
                        >
                            <i className="bi bi-file-earmark-excel-fill"></i>
                            Exportar a Excel (4 Hojas)
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-danger fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsDangerModalOpen(true)}
                        >
                            <i className="bi bi-shield-fill-exclamation"></i>
                            Zona Peligrosa
                        </button>
                    </div>
                </div>

                {/* 1. Fila de Telemetría y Métricas en Vivo (10 KPIs) */}
                <QuickReactionPersonalMetricsRow
                    metrics={funnelMetrics}
                    totalParticipants={participants.length}
                />

                {/* 2. Acordeones de Control y Ajustes */}
                <div className="row g-4 mb-4">
                    {/* Acordeón 1: Live Broadcast Overlay */}
                    <div className="col-12 col-xl-6">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div
                                className="card-header bg-dark text-white p-3 d-flex justify-content-between align-items-center cursor-pointer"
                                onClick={() => setIsOverlayAccordionOpen(!isOverlayAccordionOpen)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-tv text-warning fs-5"></i>
                                    <h6 className="fw-bold mb-0">Control de Live Overlay (OBS / vMix)</h6>
                                    <span className={`badge ${overlayConfig.overlayVisible ? 'bg-success' : 'bg-secondary'} ms-2`}>
                                        {overlayConfig.overlayVisible ? 'En Vivo' : 'Oculto'}
                                    </span>
                                </div>
                                <i className={`bi bi-chevron-${isOverlayAccordionOpen ? 'up' : 'down'}`}></i>
                            </div>

                            {isOverlayAccordionOpen && (
                                <div className="card-body p-4 bg-light">
                                    {/* Interruptor Principal de Visibilidad */}
                                    <div className="mb-4 text-center bg-white p-3 rounded-3 border">
                                        <p className="fs-6 mb-2">
                                            Estatus en Pantalla: <strong className={overlayConfig.overlayVisible ? 'text-success' : 'text-danger'}>
                                                {overlayConfig.overlayVisible ? 'Transmitiendo en Vivo' : 'Oculto (Zero-Canvas)'}
                                            </strong>
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => handleToggleOverlayVisibility(!overlayConfig.overlayVisible)}
                                            className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm ${
                                                overlayConfig.overlayVisible
                                                    ? 'btn-danger'
                                                    : 'btn-success'
                                            }`}
                                        >
                                            <i className={`bi ${overlayConfig.overlayVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'} fs-5`}></i>
                                            {overlayConfig.overlayVisible ? 'Ocultar Overlay de Pantallas' : 'Mostrar Overlay en Pantallas'}
                                        </button>
                                    </div>

                                    {/* Modo de Live Overlay */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">Modo del Cintillo</label>
                                            <select
                                                className="form-select fw-semibold"
                                                value={overlayConfig.overlayMode}
                                                onChange={(e) => setOverlayConfig({ ...overlayConfig, overlayMode: e.target.value })}
                                            >
                                                <option value="invitation">📱 Modo Invitación (Códigos QR)</option>
                                                <option value="winner">🏆 Modo Ganador (Récord en Vivo)</option>
                                            </select>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">Cantidad de Códigos QR</label>
                                            <select
                                                className="form-select fw-semibold"
                                                value={overlayConfig.overlayQrCount}
                                                onChange={(e) => setOverlayConfig({ ...overlayConfig, overlayQrCount: parseInt(e.target.value, 10) || 1 })}
                                            >
                                                <option value={1}>1 Código QR (Estilo Rooster)</option>
                                                <option value={2}>2 Códigos QR (Estilo Convivencia)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Slider de Desplazamiento Vertical (OffsetY) */}
                                    <div className="mb-3 bg-white p-3 rounded-3 border">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label fw-bold text-dark small mb-0">
                                                Posición Vertical (Offset Y): <span className="text-danger fw-bold">{overlayConfig.overlayOffsetY} px</span>
                                            </label>
                                            <button
                                                type="button"
                                                className="btn btn-link text-secondary p-0 small text-decoration-none"
                                                onClick={() => {
                                                    setOverlayConfig({ ...overlayConfig, overlayOffsetY: 0 });
                                                    throttledSaveOverlayOffset(0, overlayConfig);
                                                }}
                                            >
                                                Restablecer a 0px
                                            </button>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="-100"
                                            max="300"
                                            step="5"
                                            value={overlayConfig.overlayOffsetY}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value, 10) || 0;
                                                setOverlayConfig({ ...overlayConfig, overlayOffsetY: val });
                                                throttledSaveOverlayOffset(val, overlayConfig);
                                            }}
                                        />
                                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                                            <span>-100px (Más abajo)</span>
                                            <span>0px (Al ras inferior)</span>
                                            <span>+300px (Elevado)</span>
                                        </div>
                                    </div>

                                    {/* Subida Rápida de Cintillos SkiaSharp */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">Cintillo 1536px</label>
                                            <input
                                                type="file"
                                                className="form-control form-control-sm"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) handleUploadOverlayBanner(e.target.files[0], 'banner_1536');
                                                }}
                                            />
                                            {overlayConfig.overlayBanner1536Url && (
                                                <small className="text-success d-block text-truncate mt-1">
                                                    ✓ Cargado: {overlayConfig.overlayBanner1536Url}
                                                </small>
                                            )}
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">Cintillo 1920px</label>
                                            <input
                                                type="file"
                                                className="form-control form-control-sm"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) handleUploadOverlayBanner(e.target.files[0], 'banner_1920');
                                                }}
                                            />
                                            {overlayConfig.overlayBanner1920Url && (
                                                <small className="text-success d-block text-truncate mt-1">
                                                    ✓ Cargado: {overlayConfig.overlayBanner1920Url}
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-dark w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        onClick={handleSaveOverlaySettings}
                                        disabled={isLoading}
                                    >
                                        <i className="bi bi-floppy-fill"></i> Guardar Configuración de Live Overlay
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Acordeón 2: Físicas y Reglas de Juego */}
                    <div className="col-12 col-xl-6">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div
                                className="card-header bg-dark text-white p-3 d-flex justify-content-between align-items-center cursor-pointer"
                                onClick={() => setIsPhysicsAccordionOpen(!isPhysicsAccordionOpen)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-sliders text-warning fs-5"></i>
                                    <h6 className="fw-bold mb-0">Físicas, Anti-Spam y Reglas de Juego</h6>
                                </div>
                                <i className={`bi bi-chevron-${isPhysicsAccordionOpen ? 'up' : 'down'}`}></i>
                            </div>

                            {isPhysicsAccordionOpen && (
                                <form onSubmit={handleSaveGamePhysics} className="card-body p-4 bg-light">
                                    {/* Estrategia Anti-Spam */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold text-dark small">
                                            Estrategia de Toques Anticipados (Anti-Spam)
                                        </label>
                                        <select
                                            className="form-select fw-semibold"
                                            value={gameConfig.earlyFaultStrategy}
                                            onChange={(e) => setGameConfig({ ...gameConfig, earlyFaultStrategy: e.target.value })}
                                        >
                                            <option value="NONE">1. NONE — Aviso informativo (Sin penalización)</option>
                                            <option value="THROTTLE">2. THROTTLE — Enfriamiento (Cooldown de 350ms)</option>
                                            <option value="RESET_DELAY">3. RESET_DELAY — Reiniciar espera aleatoria</option>
                                            <option value="TIME_PENALTY">4. TIME_PENALTY — Penalización de +100ms acumulables</option>
                                            <option value="STRICT_FAULT">5. STRICT_FAULT — Descalificación inmediata</option>
                                        </select>
                                    </div>

                                    {/* Sliders de Delays de Tensión */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">
                                                Espera Mínima: <span className="text-danger fw-bold">{gameConfig.minWaitDelayMs} ms</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="1000"
                                                max="4000"
                                                step="250"
                                                value={gameConfig.minWaitDelayMs}
                                                onChange={(e) => setGameConfig({ ...gameConfig, minWaitDelayMs: parseInt(e.target.value, 10) })}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">
                                                Espera Máxima: <span className="text-danger fw-bold">{gameConfig.maxWaitDelayMs} ms</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="4000"
                                                max="10000"
                                                step="500"
                                                value={gameConfig.maxWaitDelayMs}
                                                onChange={(e) => setGameConfig({ ...gameConfig, maxWaitDelayMs: parseInt(e.target.value, 10) })}
                                            />
                                        </div>
                                    </div>

                                    {/* Timeout y Umbral Fisiológico */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">
                                                Timeout Inactividad: <span className="text-danger fw-bold">{gameConfig.inactivityTimeoutSeconds} s</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="5"
                                                max="15"
                                                step="1"
                                                value={gameConfig.inactivityTimeoutSeconds}
                                                onChange={(e) => setGameConfig({ ...gameConfig, inactivityTimeoutSeconds: parseInt(e.target.value, 10) })}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold text-dark small">
                                                Filtro Humano Biológico (ms)
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="80"
                                                max="200"
                                                value={gameConfig.minHumanReactionThresholdMs}
                                                onChange={(e) => setGameConfig({ ...gameConfig, minHumanReactionThresholdMs: parseInt(e.target.value, 10) || 120 })}
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Tiempos &lt; {gameConfig.minHumanReactionThresholdMs}ms se marcan como Descalificados.
                                            </small>
                                        </div>
                                    </div>

                                    {/* Switches de Comportamiento */}
                                    <div className="bg-white p-3 rounded-3 border mb-3">
                                        <div className="form-check form-switch mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="allowSharedDeviceSwitch"
                                                checked={gameConfig.allowSharedDevice}
                                                onChange={(e) => setGameConfig({ ...gameConfig, allowSharedDevice: e.target.checked })}
                                            />
                                            <label className="form-check-label fw-bold text-dark small" htmlFor="allowSharedDeviceSwitch">
                                                Permitir Dispositivo Compartido ("¿Alguien más quiere jugar en este teléfono?")
                                            </label>
                                        </div>

                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="earlyFaultDetectionSwitch"
                                                checked={gameConfig.allowEarlyFaultDetection}
                                                onChange={(e) => setGameConfig({ ...gameConfig, allowEarlyFaultDetection: e.target.checked })}
                                            />
                                            <label className="form-check-label fw-bold text-dark small" htmlFor="earlyFaultDetectionSwitch">
                                                Detección Estricta de Falso Inicio (Sprite Gris / Derrota Inmediata)
                                            </label>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-danger w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        disabled={isLoading}
                                    >
                                        <i className="bi bi-floppy-fill"></i> Guardar Físicas y Reglas
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Acordeón 3: Gestor de Temas y Sprites */}
                    <div className="col-12">
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                            <div
                                className="card-header bg-dark text-white p-3 d-flex justify-content-between align-items-center cursor-pointer"
                                onClick={() => setIsThemesAccordionOpen(!isThemesAccordionOpen)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-palette-fill text-warning fs-5"></i>
                                    <h6 className="fw-bold mb-0">Gestión de Tema Activo y Sprites Gráficos</h6>
                                    <span className="badge bg-light text-dark ms-2">
                                        {activeTheme ? activeTheme.name : 'Tema Predeterminado'}
                                    </span>
                                </div>
                                <i className={`bi bi-chevron-${isThemesAccordionOpen ? 'up' : 'down'}`}></i>
                            </div>

                            {isThemesAccordionOpen && (
                                <div className="card-body p-4 bg-light">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                                        <div>
                                            <h6 className="fw-bold text-dark mb-1">
                                                Tema Activo: <span className="text-danger">{activeTheme?.name || 'Tema Oficial'}</span>
                                            </h6>
                                            <p className="text-muted small mb-0">
                                                Configura los fondos, logos y los 4 sprites del personaje procesados por SkiaSharp.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-danger fw-bold d-flex align-items-center gap-2 shadow-sm"
                                            onClick={() => setIsThemeModalOpen(true)}
                                        >
                                            <i className="bi bi-palette"></i>
                                            Abrir Gestor de Temas ({themesList.length})
                                        </button>
                                    </div>

                                    {/* Miniaturas de vista previa */}
                                    {activeTheme && (
                                        <div className="row g-2 bg-white p-3 rounded-3 border">
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>1. Espalda</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.spriteBackUrl)}
                                                    alt="Sprite 1"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>2. Frente</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.spriteFrontUrl)}
                                                    alt="Sprite 2"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>3. Disparo</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.spriteShootUrl)}
                                                    alt="Sprite 3"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>4. Victoria</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.spriteVictoryUrl)}
                                                    alt="Sprite 4"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Logo</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.logoImageUrl)}
                                                    alt="Logo"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                            <div className="col-4 col-md-2 text-center">
                                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Fondo</span>
                                                <img
                                                    src={resolveMediaUrl(activeTheme.backgroundImageUrl)}
                                                    alt="Fondo"
                                                    className="img-fluid rounded border bg-light"
                                                    style={{ maxHeight: '60px' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. Tabla y Padrón de Participantes */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                    <div className="card-header bg-white p-3 border-bottom">
                        <div className="row g-3 align-items-center justify-content-between">
                            <div className="col-12 col-md-4">
                                <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <i className="bi bi-people-fill text-danger"></i>
                                    Padrón de Participantes y Clasificación
                                    <span className="badge bg-danger-subtle text-danger fs-6 fw-bold">
                                        {filteredParticipants.length}
                                    </span>
                                </h5>
                            </div>

                            {/* Controles de Búsqueda, Filtros y PII Toggle */}
                            <div className="col-12 col-md-8 d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
                                <div className="input-group" style={{ maxWidth: '240px' }}>
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Buscar aficionado..."
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>

                                <select
                                    className="form-select form-select-sm fw-semibold"
                                    style={{ maxWidth: '170px' }}
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                >
                                    <option value="all">Todos los registros</option>
                                    <option value="winners">🏆 Ganadores / Récords</option>
                                    <option value="early_loss">⚠️ Toques anticipados</option>
                                    <option value="disqualified">🛑 Descalificados</option>
                                    <option value="retries">🔄 Con reintento</option>
                                </select>

                                <button
                                    type="button"
                                    className={`btn btn-sm fw-bold d-flex align-items-center gap-1 ${
                                        maskPii ? 'btn-outline-secondary' : 'btn-outline-warning'
                                    }`}
                                    onClick={() => setMaskPii(!maskPii)}
                                    title={maskPii ? 'Mostrar datos sensibles' : 'Ocultar datos sensibles (PII)'}
                                >
                                    <i className={`bi ${maskPii ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    {maskPii ? 'PII Protegido' : 'PII Visible'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tabla Responsiva */}
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light small text-uppercase text-secondary">
                                <tr>
                                    <th className="text-center" style={{ width: '60px' }}>#</th>
                                    <th>Aficionado</th>
                                    <th>Contacto</th>
                                    <th className="text-center">Tiempo Neto</th>
                                    <th className="text-center">Penaliz.</th>
                                    <th className="text-center">Total ms</th>
                                    <th className="text-center">Insignia</th>
                                    <th className="text-center">Estado</th>
                                    <th className="text-center">Fecha</th>
                                    <th className="text-center" style={{ width: '130px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedParticipants.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5 text-muted">
                                            <i className="bi bi-inbox fs-2 d-block mb-2 text-secondary"></i>
                                            No se encontraron participantes con los filtros seleccionados.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedParticipants.map((p, index) => {
                                        const rankNumber = startIndex + index + 1;
                                        const isRecordHolder = p.isWinner || (rankNumber === 1 && p.outcomeStatus === 'WIN');
                                        const emailDisplay = maskPii ? MaskEmail(p.email) : p.email;
                                        const phoneDisplay = maskPii ? MaskPhone(p.phone) : p.phone;

                                        let badgeColorClass = 'bg-secondary';
                                        if (p.badgeName?.includes('Divino') || p.badgeName?.includes('Halcón')) badgeColorClass = 'bg-primary';
                                        else if (p.badgeName?.includes('Felinos')) badgeColorClass = 'bg-success';
                                        else if (p.badgeName?.includes('Pistolero')) badgeColorClass = 'bg-info text-dark';
                                        else if (p.badgeName?.includes('Humano')) badgeColorClass = 'bg-warning text-dark';
                                        else if (p.badgeName?.includes('Agotado') || p.outcomeStatus === 'DISQUALIFIED') badgeColorClass = 'bg-danger';

                                        return (
                                            <tr key={p.id} className={isRecordHolder ? 'table-warning-subtle fw-semibold' : ''}>
                                                <td className="text-center fw-bold text-secondary">
                                                    {isRecordHolder ? '🏆 1' : rankNumber}
                                                </td>

                                                <td>
                                                    <div className="fw-bold text-dark">
                                                        {p.participantName || p.name}
                                                    </div>
                                                    {p.resetByAdmin && (
                                                        <span className="badge bg-warning text-dark small" style={{ fontSize: '0.65rem' }}>
                                                            <i className="bi bi-arrow-repeat me-1"></i> Reintento Concedido
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="small text-muted">
                                                    <div><i className="bi bi-envelope me-1"></i>{emailDisplay}</div>
                                                    <div><i className="bi bi-telephone me-1"></i>{phoneDisplay}</div>
                                                </td>

                                                <td className="text-center font-monospace fw-bold fs-6">
                                                    {p.reactionTimeMs != null ? `${p.reactionTimeMs} ms` : '---'}
                                                </td>

                                                <td className="text-center font-monospace text-danger small">
                                                    {p.penaltyTimeMs ? `+${p.penaltyTimeMs} ms` : '0 ms'}
                                                </td>

                                                <td className="text-center font-monospace fw-bold text-dark fs-6">
                                                    {p.reactionTimeMs != null ? `${(p.reactionTimeMs || 0) + (p.penaltyTimeMs || 0)} ms` : '---'}
                                                </td>

                                                <td className="text-center">
                                                    <span className={`badge ${badgeColorClass} small px-2 py-1`}>
                                                        {p.badgeName || 'Pendiente'}
                                                    </span>
                                                </td>

                                                <td className="text-center">
                                                    {p.outcomeStatus === 'WIN' ? (
                                                        <span className="badge bg-success-subtle text-success fw-bold">✓ Válido</span>
                                                    ) : p.outcomeStatus === 'DISQUALIFIED' ? (
                                                        <span className="badge bg-danger-subtle text-danger fw-bold">🛑 Descalificado</span>
                                                    ) : p.outcomeStatus === 'EARLY_LOSS' ? (
                                                        <span className="badge bg-warning-subtle text-warning fw-bold">⚠️ Falso Inicio</span>
                                                    ) : (
                                                        <span className="badge bg-light text-secondary">Pendiente</span>
                                                    )}
                                                </td>

                                                <td className="text-center small text-muted">
                                                    {p.playedAtUtc ? new Date(p.playedAtUtc).toLocaleTimeString('es-MX', { hour12: false }) : '---'}
                                                </td>

                                                <td className="text-center">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-warning fw-bold d-flex align-items-center gap-1 mx-auto"
                                                        onClick={() => setResetTargetParticipant(p)}
                                                        title="Conceder otra oportunidad al aficionado"
                                                    >
                                                        <i className="bi bi-arrow-repeat"></i>
                                                        <span className="d-none d-md-inline">Reintento</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    <div className="card-footer bg-white border-top p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="text-muted small">
                            Mostrando {startIndex + 1} a {Math.min(startIndex + pageSize, totalRegistries)} de {totalRegistries} aficionados
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '80px' }}
                                value={pageSize}
                                onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            >
                                Anterior
                            </button>
                            <span className="small text-secondary fw-bold px-2">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};
